import { hotkeySections } from '../model/hotkeys.js';
import type { LinkView } from '../model/connection.js';
import type { TeamCellView } from '../model/orders.js';

/**
 * The status bar (design 1635–1724).
 *
 * Carries the link state and latency, the hotkey map, and the team's readiness.
 * It does **not** carry `consoleId` — Q16 dropped it, and the row is better for
 * the space.
 *
 * Team readiness lives here rather than in a panel because it is the comms-free
 * coordination aid (shell.md §Orders): the reason to look at it is to find the
 * cell that is holding the turn up, and that has to be answerable without
 * leaving whatever window you are working in.
 */

export interface StatusBarProps {
  readonly link: LinkView;
  readonly team: readonly TeamCellView[];
  readonly hotkeysOpen: boolean;
  readonly onToggleHotkeys: () => void;
  /** The tab in front of the operator. The SDA block is shown only on its own board. */
  readonly tab: string | null;
}

const TONE_COLOUR = {
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  bad: 'var(--red)',
} as const;

const READINESS_COLOUR = {
  READY: 'var(--ok)',
  WORKING: 'var(--warn-dot)',
  EMPTY: 'var(--text-disabled)',
} as const;

export function StatusBar(props: StatusBarProps): JSX.Element {
  return (
    <div
      data-mat="chrome"
      style={{
        position: 'relative',
        flex: 'none',
        height: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '0 12px',
        background: 'var(--surface-2)',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div
        data-testid="team-strip"
        style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden' }}
      >
        {props.team.map((cell) => (
          <span
            key={cell.cell}
            data-testid={`team-cell-${cell.cell}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              letterSpacing: '.08em',
              whiteSpace: 'nowrap',
              // An EMPTY seat is a fact the team acts on — who is unmanned — so it
              // reads at --text-muted like the rest of the strip rather than in the
              // disabled tier.
              color: 'var(--text-muted)',
            }}
          >
            <span
              aria-hidden="true"
              style={{ width: 5, height: 5, background: READINESS_COLOUR[cell.readiness] }}
            />
            {cell.cell} {cell.readiness}
            {/*
              A claimed seat whose player has dropped is not the same as one still
              working, and the team needs to tell them apart before deciding whom to
              chase. That used to be said with `opacity: .5`, which multiplied against
              --text-muted and put this line at 2.11:1 in light and 2.63:1 in dark —
              under half the floor, on the row that reports the state of the room. It
              is a word now, in the colour the console already uses for a dropped link.
            */}
            {cell.readiness !== 'EMPTY' && !cell.connected && (
              <span style={{ color: 'var(--warn)', fontWeight: 600 }}>· DROPPED</span>
            )}
          </span>
        ))}
      </div>

      <span style={{ flex: 1, minWidth: 0 }} />

      <button
        type="button"
        data-testid="hotkeys-toggle"
        aria-expanded={props.hotkeysOpen}
        onClick={props.onToggleHotkeys}
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 18,
          padding: '0 7px',
          background: props.hotkeysOpen ? 'var(--accent-tint)' : 'transparent',
          border: `1px solid ${props.hotkeysOpen ? 'var(--accent-border)' : 'var(--border)'}`,
          color: props.hotkeysOpen ? 'var(--accent)' : 'var(--text-muted)',
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          letterSpacing: '.1em',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: 10, lineHeight: 1 }}>?</span>
        <span>HOTKEYS</span>
      </button>

      {props.hotkeysOpen && <HotkeyPanel tab={props.tab} onClose={props.onToggleHotkeys} />}

      <span style={{ flex: 'none', width: 1, height: 12, background: 'var(--rule)' }} />
      <span
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        LINK
      </span>
      <span
        aria-hidden="true"
        style={{ flex: 'none', width: 4, height: 12, background: TONE_COLOUR[props.link.tone] }}
      />
      <span
        data-testid="link-state"
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '.08em',
          whiteSpace: 'nowrap',
          color: TONE_COLOUR[props.link.tone],
        }}
      >
        {props.link.latencyMs === null
          ? props.link.token
          : `${props.link.token} · ${props.link.latencyMs} MS`}
      </span>
    </div>
  );
}

function HotkeyPanel(props: { tab: string | null; onClose: () => void }): JSX.Element {
  return (
    <div
      data-testid="hotkeys"
      role="dialog"
      aria-label="Keyboard map"
      style={{
        position: 'absolute',
        right: 12,
        bottom: 29,
        zIndex: 'var(--z-overlay)',
        /*
          Two columns, because one did not fit the console's own floor.
          At 1280x720 the panel was 326px wide and clipped through the middle of
          a row, hiding UNDO and the whole ANYWHERE section — including the key
          that opens it — with no scroll affordance to say so. This is the only
          discoverability surface in the product and it could not display itself.
          Twenty-five rows in two columns is ~260px tall; the maxHeight below is
          now a backstop rather than the thing doing the work.
        */
        width: 620,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        columnCount: 2,
        columnGap: 18,
        padding: '10px 12px 12px',
        background: 'var(--surface-3)',
        border: '1px solid var(--border-strong)',
        boxShadow: 'var(--shadow)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          paddingBottom: 8,
          marginBottom: 8,
          columnSpan: 'all',
          borderBottom: '1px solid var(--rule)',
        }}
      >
        <span
          style={{
            flex: 1,
            fontFamily: 'var(--font-ui)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          Keyboard
        </span>
        <button
          type="button"
          data-testid="hotkeys-close"
          aria-label="Close"
          onClick={props.onClose}
          style={{
            width: 16,
            height: 16,
            padding: 0,
            background: 'transparent',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-data)',
            fontSize: 10,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {hotkeySections(props.tab).map((section) => (
        <div key={section.title} style={{ breakInside: 'avoid' }}>
          <span
            style={{
              display: 'block',
              margin: '9px 0 5px',
              fontFamily: 'var(--font-ui)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            {section.title}
          </span>
          <div
            style={{
              display: 'grid',
              // 92, not 78: RIGHT-CLICK and SHIFT CLICK wrapped at the old width.
              gridTemplateColumns: '92px 1fr',
              gap: '5px 10px',
              alignItems: 'baseline',
            }}
          >
            {section.rows.map((row) => (
              <HotkeyRowCells key={row.keys} keys={row.keys} action={row.action} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** One grid row — two cells, so the grid keeps its columns rather than nesting. */
function HotkeyRowCells(props: { keys: string; action: string }): JSX.Element {
  return (
    <>
      <span
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '.08em',
          color: 'var(--text)',
        }}
      >
        {props.keys}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 10.5,
          letterSpacing: '.04em',
          color: 'var(--text-2)',
        }}
      >
        {props.action}
      </span>
    </>
  );
}
